import React from 'react'
import { Icon, Badge, AnalysisMainMetricCard } from '../../../atoms'
import { InfoTooltip } from '../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../TooltipMetricExplanation'
import {
  Grid,
  Card,
  CardHeader,
  CardTitle,
  MetricGrid,
  MetricRow,
  MetricLabel,
  MetricValue,
  MetricHighlight,
} from '../GeoAnalysisSection.styles'
import { calculateTerrainMetrics } from '../utils'
import type { OverviewTabProps } from '../GeoAnalysisSection.types'

/**
 * Overview tab component showing geometry and terrain metrics
 */
export const OverviewTab: React.FC<OverviewTabProps> = ({
  metrics,
  altimetryData,
  terrainAltimetryData,
  translations,
}) => {
  // LiDAR ground truth is authoritative for the slope/elevation NUMBERS when present; otherwise
  // fall back to the API altimetry grid (unchanged behaviour for parcels without coverage).
  const terrainData = terrainAltimetryData ?? altimetryData
  const terrainMetrics = terrainData ? calculateTerrainMetrics(terrainData) : null

  // Helper to translate English aspect names to current language
  const translateAspect = (aspect: string | undefined): string => {
    if (!aspect) return translations?.status?.na || 'N/A'
    const dirMap: Record<string, string | undefined> = {
      North: translations?.directions?.north,
      'North-East': translations?.directions?.northEast,
      East: translations?.directions?.east,
      'South-East': translations?.directions?.southEast,
      South: translations?.directions?.south,
      'South-West': translations?.directions?.southWest,
      West: translations?.directions?.west,
      'North-West': translations?.directions?.northWest,
      Flat: translations?.directions?.flat,
      Square: translations?.directions?.square,
    }
    return dirMap[aspect] || aspect
  }

  // Helper function to get status text based on slope
  const getSlopeStatus = (slope: number) => {
    if (slope < 5) return translations?.status?.excellent || 'Excellent'
    if (slope < 15) return translations?.status?.moderate || 'Moderate'
    return translations?.status?.challenging || 'Challenging'
  }

  // Helper function to get construction suitability text
  const getConstructionSuitability = (slope: number) => {
    if (slope < 5) return translations?.status?.excellent || 'Excellent'
    if (slope < 10) return translations?.status?.good || 'Good'
    return translations?.status?.challenging || 'Challenging'
  }

  return (
    <>
      <Grid $columns={3}>
        {/* Main metrics cards */}
        <AnalysisMainMetricCard
          iconName="square"
          iconBackgroundColor="rgba(0, 179, 65, 0.1)"
          value={`${metrics.area.toLocaleString()} m²`}
          label={
            <>
              {translations?.metrics?.totalArea || 'Total Area'}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={translations?.tooltips?.totalArea?.title || 'Total Area'}
                  shortDescription={
                    translations?.tooltips?.totalArea?.shortDescription ||
                    'The cadastral area of the land parcel'
                  }
                  calculation={
                    translations?.tooltips?.totalArea?.calculation ||
                    'Computed from official cadastral polygon boundaries using Swiss coordinate system (CH1903+/LV95)'
                  }
                  importance={
                    translations?.tooltips?.totalArea?.importance ||
                    'This is the legally registered area used for property taxes, building permits, and land transactions'
                  }
                  limitations={
                    translations?.tooltips?.totalArea?.limitations ||
                    'This is the official area from the land registry and may differ slightly from measured or surveyed areas'
                  }
                  source={
                    translations?.tooltips?.totalArea?.source ||
                    'Federal Office of Topography swisstopo'
                  }
                />
              </InfoTooltip>
            </>
          }
          variant={{
            type: 'progressBar',
            percentage: 100,
            color: '#00B341',
          }}
        />

        <AnalysisMainMetricCard
          iconName="maximize-2"
          iconBackgroundColor="rgba(2, 148, 223, 0.1)"
          value={`${Math.round(metrics.perimeter)} m`}
          label={
            <>
              {translations?.metrics?.perimeter || 'Perimeter'}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={translations?.tooltips?.perimeter?.title || 'Perimeter'}
                  shortDescription={
                    translations?.tooltips?.perimeter?.shortDescription ||
                    'The total boundary length of the parcel'
                  }
                  calculation={
                    translations?.tooltips?.perimeter?.calculation ||
                    'Sum of all boundary segment lengths using geodesic calculations for accuracy'
                  }
                  importance={
                    translations?.tooltips?.perimeter?.importance ||
                    'Affects fencing costs, boundary maintenance, and edge-to-area ratio. Longer perimeters relative to area indicate more irregular shapes'
                  }
                  limitations={
                    translations?.tooltips?.perimeter?.limitations ||
                    'Complex boundaries with many vertices will have longer perimeters and potentially higher development costs'
                  }
                  source={
                    translations?.tooltips?.perimeter?.source ||
                    'Federal Office of Topography swisstopo'
                  }
                />
              </InfoTooltip>
            </>
          }
          variant={{
            type: 'progressBar',
            percentage: metrics.shapeFactor * 100,
            color: '#0294df',
          }}
        />

        <AnalysisMainMetricCard
          iconName="trending-up"
          iconBackgroundColor="rgba(255, 149, 0, 0.1)"
          value={
            altimetryData && terrainMetrics
              ? `${terrainMetrics.averageSlope.toFixed(1)}°`
              : translations?.status?.na || 'N/A'
          }
          label={
            <>
              {translations?.metrics?.averageSlope || 'Average Slope'}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={translations?.tooltips?.averageSlope?.title || 'Average Slope'}
                  shortDescription={
                    translations?.tooltips?.averageSlope?.shortDescription ||
                    'Mean terrain inclination across the entire parcel'
                  }
                  calculation={
                    translations?.tooltips?.averageSlope?.calculation ||
                    'Computed from SwissAlti3D elevation data on a 5-meter grid. Slope calculated between adjacent grid points in 8 directions, then averaged'
                  }
                  categories={
                    translations?.tooltips?.averageSlope?.categories || [
                      {
                        label: 'Flat',
                        range: '0-5°',
                        description: 'Ideal for construction',
                      },
                      {
                        label: 'Gentle slope',
                        range: '5-15°',
                        description: 'Manageable with minor grading',
                      },
                      {
                        label: 'Moderate slope',
                        range: '15-25°',
                        description: 'Requires terracing or retaining walls',
                      },
                      {
                        label: 'Steep',
                        range: '>25°',
                        description: 'Significant engineering required',
                      },
                    ]
                  }
                  limitations={
                    translations?.tooltips?.averageSlope?.limitations ||
                    'Based on 5m grid resolution. Small-scale variations and extreme slopes between grid points may not be captured. Actual site conditions should be verified'
                  }
                  source={
                    translations?.tooltips?.averageSlope?.source ||
                    'SwissAlti3D - Federal Office of Topography swisstopo'
                  }
                />
              </InfoTooltip>
            </>
          }
          variant={
            altimetryData && terrainMetrics
              ? {
                  type: 'highlight',
                  highlightType:
                    terrainMetrics.averageSlope < 5
                      ? 'success'
                      : terrainMetrics.averageSlope < 15
                        ? 'warning'
                        : 'danger',
                  text: getSlopeStatus(terrainMetrics.averageSlope),
                }
              : { type: 'none' }
          }
        />
      </Grid>

      <Grid $columns={3}>
        {/* Geometry Details */}
        <Card>
          <CardHeader>
            <CardTitle>
              <Icon name="hexagon" size="small" />
              {translations?.sections?.geometryAnalysis || 'Geometry Analysis'}
            </CardTitle>
          </CardHeader>
          <MetricGrid>
            <MetricRow>
              <MetricLabel>
                {translations?.metrics?.shapeRegularity || 'Shape Regularity'}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={translations?.tooltips?.shapeRegularity?.title || 'Shape Regularity'}
                    shortDescription={
                      translations?.tooltips?.shapeRegularity?.shortDescription ||
                      'Classification based on geometric efficiency'
                    }
                    calculation={
                      translations?.tooltips?.shapeRegularity?.calculation ||
                      'Uses shape factor (4π × Area / Perimeter²). Perfect circle = 1.0, square = 0.785, irregular shapes < 0.5'
                    }
                    categories={
                      translations?.tooltips?.shapeRegularity?.categories || [
                        {
                          label: 'Regular',
                          range: '>0.7',
                          description: 'Efficient shape, easy to develop',
                        },
                        {
                          label: 'Moderate',
                          range: '0.5-0.7',
                          description: 'Some irregularity, standard planning',
                        },
                        {
                          label: 'Irregular',
                          range: '<0.5',
                          description: 'Complex shape, challenging layout',
                        },
                      ]
                    }
                    importance={
                      translations?.tooltips?.shapeRegularity?.importance ||
                      'Regular shapes maximize usable space, simplify construction, reduce fencing costs, and allow better building placement'
                    }
                    limitations={
                      translations?.tooltips?.shapeRegularity?.limitations ||
                      'Very irregular shapes may have unusable corners or require creative architectural solutions'
                    }
                    source={
                      translations?.tooltips?.shapeRegularity?.source ||
                      'Federal Office of Topography swisstopo'
                    }
                  />
                </InfoTooltip>
              </MetricLabel>
              <MetricValue>
                {metrics.regularity === 'Regular'
                  ? translations?.status?.regular || 'Regular'
                  : metrics.regularity === 'Moderate'
                    ? translations?.status?.moderate || 'Moderate'
                    : translations?.status?.irregular || 'Irregular'}
              </MetricValue>
            </MetricRow>
            <MetricRow>
              <MetricLabel>
                {translations?.metrics?.compactnessScore || 'Compactness Score'}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={translations?.tooltips?.compactnessScore?.title || 'Compactness Score'}
                    shortDescription={
                      translations?.tooltips?.compactnessScore?.shortDescription ||
                      'Geometric efficiency relative to ideal shape'
                    }
                    calculation={
                      translations?.tooltips?.compactnessScore?.calculation ||
                      'Ratio of actual area to the area of a circle with the same perimeter. Formula: 4π × (Area/Perimeter²) × 100%'
                    }
                    categories={
                      translations?.tooltips?.compactnessScore?.categories || [
                        {
                          label: 'Highly compact',
                          range: '80-100%',
                          description: 'Excellent shape efficiency',
                        },
                        {
                          label: 'Good compactness',
                          range: '60-80%',
                          description: 'Efficient shape for development',
                        },
                        {
                          label: 'Moderate',
                          range: '40-60%',
                          description: 'Some inefficiency in shape',
                        },
                        {
                          label: 'Poor',
                          range: '<40%',
                          description: 'Very elongated or irregular',
                        },
                      ]
                    }
                    importance={
                      translations?.tooltips?.compactnessScore?.importance ||
                      'Lower infrastructure costs, efficient land use, better security, easier maintenance'
                    }
                    limitations={
                      translations?.tooltips?.compactnessScore?.limitations ||
                      'Low compactness may indicate narrow strips or irregular extensions that limit development options'
                    }
                    source={
                      translations?.tooltips?.compactnessScore?.source ||
                      'Federal Office of Topography swisstopo'
                    }
                  />
                </InfoTooltip>
              </MetricLabel>
              <MetricValue>{(metrics.compactness * 100).toFixed(0)}%</MetricValue>
            </MetricRow>
            <MetricRow>
              <MetricLabel>
                {translations?.metrics?.dominantOrientation || 'Dominant Orientation'}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={
                      translations?.tooltips?.dominantOrientation?.title || 'Dominant Orientation'
                    }
                    shortDescription={
                      translations?.tooltips?.dominantOrientation?.shortDescription ||
                      'Primary compass direction of terrain slope'
                    }
                    calculation={
                      translations?.tooltips?.dominantOrientation?.calculation ||
                      'Analyzes elevation gradients across the parcel to determine the predominant downslope direction. Weighted by slope steepness'
                    }
                    categories={
                      translations?.tooltips?.dominantOrientation?.categories || [
                        {
                          label: 'South',
                          description: 'Maximum solar gain, ideal for passive heating',
                        },
                        {
                          label: 'North',
                          description: 'Minimal direct sun, cooler, good for summer comfort',
                        },
                        {
                          label: 'East',
                          description: 'Morning sun, quick warming',
                        },
                        {
                          label: 'West',
                          description: 'Afternoon sun, potential overheating',
                        },
                        {
                          label: 'Flat',
                          description: 'No dominant slope (avg slope <2°)',
                        },
                      ]
                    }
                    importance={
                      translations?.tooltips?.dominantOrientation?.importance ||
                      'Affects solar panel efficiency, heating/cooling costs, garden planning, outdoor living spaces'
                    }
                    limitations={
                      translations?.tooltips?.dominantOrientation?.limitations ||
                      'Local variations may differ. Consider micro-climates and surrounding structures'
                    }
                    source={
                      translations?.tooltips?.dominantOrientation?.source ||
                      'SwissAlti3D - Federal Office of Topography swisstopo'
                    }
                  />
                </InfoTooltip>
              </MetricLabel>
              <MetricHighlight
                $textAlign="right"
                $type={
                  (terrainMetrics?.primaryAspect || metrics.orientation)?.includes?.('South')
                    ? 'success'
                    : (terrainMetrics?.primaryAspect || metrics.orientation)?.includes?.('East') ||
                        (terrainMetrics?.primaryAspect || metrics.orientation)?.includes?.('West')
                      ? 'warning'
                      : (terrainMetrics?.primaryAspect || metrics.orientation) === 'Flat' ||
                          (terrainMetrics?.primaryAspect || metrics.orientation) === 'Square'
                        ? 'info'
                        : (terrainMetrics?.primaryAspect || metrics.orientation)?.includes?.(
                              'North'
                            )
                          ? 'danger'
                          : 'danger'
                }
              >
                {translateAspect(terrainMetrics?.primaryAspect)}
              </MetricHighlight>
            </MetricRow>
            <MetricRow>
              <MetricLabel>{translations?.metrics?.lengthWidth || 'Length × Width'}</MetricLabel>
              <MetricValue>
                {Math.round(metrics.length)} × {Math.round(metrics.width)} m
              </MetricValue>
            </MetricRow>
          </MetricGrid>
        </Card>

        {/* Altitude Profile - Real Data */}
        <Card>
          <CardHeader>
            <CardTitle>
              <Icon name="mountain" size="small" />
              {translations?.sections?.altitudeProfile || 'Altitude Profile'}
            </CardTitle>
            {altimetryData && (
              <Badge variant="success">{translations?.status?.realData || 'Real Data'}</Badge>
            )}
          </CardHeader>
          {altimetryData ? (
            <MetricGrid>
              <MetricRow>
                <MetricLabel>{translations?.metrics?.minimum || 'Minimum'}</MetricLabel>
                <MetricValue>
                  {(terrainData ?? altimetryData).altitude_min.toFixed(1)} m
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>{translations?.metrics?.maximum || 'Maximum'}</MetricLabel>
                <MetricValue>
                  {(terrainData ?? altimetryData).altitude_max.toFixed(1)} m
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>
                  {translations?.metrics?.elevationRange || 'Elevation Range'}
                </MetricLabel>
                <MetricHighlight
                  $type={
                    (terrainData ?? altimetryData).altitude_max -
                      (terrainData ?? altimetryData).altitude_min <
                    10
                      ? 'success'
                      : (terrainData ?? altimetryData).altitude_max -
                            (terrainData ?? altimetryData).altitude_min <
                          20
                        ? 'warning'
                        : 'danger'
                  }
                >
                  {(
                    (terrainData ?? altimetryData).altitude_max -
                    (terrainData ?? altimetryData).altitude_min
                  ).toFixed(1)}{' '}
                  m
                </MetricHighlight>
              </MetricRow>
              <MetricRow>
                <MetricLabel>{translations?.metrics?.dataPoints || 'Data Points'}</MetricLabel>
                <MetricValue>{altimetryData.altimetry_grid.length}</MetricValue>
              </MetricRow>
            </MetricGrid>
          ) : (
            <div
              style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#999',
                fontSize: '14px',
              }}
            >
              {translations?.noAltitudeData || 'No altitude data available'}
            </div>
          )}
        </Card>

        {/* Development Analysis - Based on Real Slope Data */}
        <Card>
          <CardHeader>
            <CardTitle>
              <Icon name="hard-hat" size="small" />
              {translations?.sections?.developmentAnalysis || 'Development Analysis'}
            </CardTitle>
            {terrainMetrics && (
              <Badge variant="success">{translations?.status?.realData || 'Real Data'}</Badge>
            )}
          </CardHeader>
          {terrainMetrics ? (
            <MetricGrid>
              <MetricRow>
                <MetricLabel>{translations?.metrics?.averageSlope || 'Average Slope'}</MetricLabel>
                <MetricValue>{terrainMetrics.averageSlope.toFixed(1)}°</MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>
                  {translations?.metrics?.maximumSlope || 'Maximum Slope'}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={translations?.tooltips?.maximumSlope?.title || 'Maximum Slope'}
                      shortDescription={
                        translations?.tooltips?.maximumSlope?.shortDescription ||
                        'Steepest terrain gradient within the parcel'
                      }
                      calculation={
                        translations?.tooltips?.maximumSlope?.calculation ||
                        'Identifies the maximum slope angle between any adjacent points in the 5m elevation grid, measured in 8 directions'
                      }
                      categories={
                        translations?.tooltips?.maximumSlope?.categories || [
                          {
                            label: 'Standard',
                            range: '<10°',
                            description: 'Standard construction methods',
                          },
                          {
                            label: 'Moderate',
                            range: '10-20°',
                            description: 'May need localized grading',
                          },
                          {
                            label: 'Challenging',
                            range: '20-30°',
                            description: 'Requires special foundation design',
                          },
                          {
                            label: 'Extreme',
                            range: '>30°',
                            description: 'Engineering challenges, possible exclusion zone',
                          },
                        ]
                      }
                      importance={
                        translations?.tooltips?.maximumSlope?.importance ||
                        'Even small areas of steep slope can affect access roads, utilities routing, and drainage design'
                      }
                      limitations={
                        translations?.tooltips?.maximumSlope?.limitations ||
                        'Based on 5m grid. Actual maximum slopes between grid points may be higher. Critical areas require detailed survey'
                      }
                      source={
                        translations?.tooltips?.maximumSlope?.source ||
                        'SwissAlti3D - Federal Office of Topography swisstopo'
                      }
                    />
                  </InfoTooltip>
                </MetricLabel>
                <MetricValue>{terrainMetrics.maxSlope.toFixed(1)}°</MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>
                  {translations?.metrics?.maximumFlatArea || 'Maximum Flat Area'}
                </MetricLabel>
                <MetricValue>
                  {terrainMetrics.maxFlatArea
                    ? `${terrainMetrics.maxFlatArea} m²`
                    : translations?.status?.na || 'N/A'}
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>
                  {translations?.metrics?.constructionSuitability || 'Construction Suitability'}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={
                        translations?.tooltips?.constructionSuitability?.title ||
                        'Construction Suitability'
                      }
                      shortDescription={
                        translations?.tooltips?.constructionSuitability?.shortDescription ||
                        'Overall assessment for building development'
                      }
                      calculation={
                        translations?.tooltips?.constructionSuitability?.calculation ||
                        'Based on average terrain slope across the entire parcel, considering Swiss construction standards'
                      }
                      categories={
                        translations?.tooltips?.constructionSuitability?.categories || [
                          {
                            label: 'Excellent',
                            range: '<5°',
                            description:
                              'Minimal site preparation, standard foundations, low earthwork costs',
                          },
                          {
                            label: 'Good',
                            range: '5-10°',
                            description:
                              'Moderate grading needed, slightly higher costs, terracing may help',
                          },
                          {
                            label: 'Challenging',
                            range: '>10°',
                            description:
                              'Significant engineering required, retaining walls needed, higher costs',
                          },
                        ]
                      }
                      importance={
                        translations?.tooltips?.constructionSuitability?.importance ||
                        'Each 5° increase in average slope can add 15-25% to site preparation costs'
                      }
                      limitations={
                        translations?.tooltips?.constructionSuitability?.limitations ||
                        'This is a general assessment. Specific building locations within the parcel may have different suitability'
                      }
                      source={
                        translations?.tooltips?.constructionSuitability?.source ||
                        'SwissAlti3D - Federal Office of Topography swisstopo'
                      }
                    />
                  </InfoTooltip>
                </MetricLabel>
                <MetricHighlight
                  $type={
                    terrainMetrics.averageSlope < 5
                      ? 'success'
                      : terrainMetrics.averageSlope < 10
                        ? 'warning'
                        : 'danger'
                  }
                >
                  {getConstructionSuitability(terrainMetrics.averageSlope)}
                </MetricHighlight>
              </MetricRow>
            </MetricGrid>
          ) : (
            <div
              style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#999',
                fontSize: '14px',
              }}
            >
              {translations?.noSlopeAnalysis || 'No slope analysis available'}
            </div>
          )}
        </Card>
      </Grid>
    </>
  )
}
